import "./ReleaseNotes.scss";
import { Box, Typography } from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import { useState, useEffect, useRef } from "react";
import AppFilterDate from "../AppFilterDate/AppFilterDate";
import React from "react";
import ReleaseNotesDetail from "./ReleaseNotesDetail/ReleaseNotesDetail";
import StatusFilter from "../StatusFilter/StatusFilter";
import useHandleAnchorClick from "../../hooks/useHandleAnchorClick";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setClickedReleaseItem, setReleaseNoteTitle, setShowReleaseDetails } from "../../redux/slices/staticPagesSlice";
import { cleanDetailsLink } from "../../constants/apiUtil";
import { startOfDay, endOfDay } from 'date-fns';

const getServiceLink = (serv) => {
  const servVar =
    typeof serv === "string" || serv instanceof String ? serv : serv?.value;
  let serviceName = servVar?.toLowerCase();
  if (serviceName === "orderview" || serviceName === "orderretrieve") {
    serviceName = "orderretrieve/functionality";
  } else if (serviceName !== "orderchangenotification") {
    serviceName = `${serviceName}/functionality`;
  }
  return serviceName;
};

const ReleaseNotes = () => {
  const { t, i18n } = useTranslation();
  const json = t('release_notes', { returnObjects: true }) as { noData: string };
  const data: any[] = t('release_notes_array', { returnObjects: true, defaultValue: [] });
  const [detailsContent, setDetailsContent] = useState<any>("");
  const [entries, setEntries] = useState(Array.isArray(data) ? data.sort(sortByPubDate) : []) as any;
  const [filterByDate, setFilterByDate] = useState();
  const [filterByService, setFilterByService] = useState<string[]>([]);
  const prevFilterByServiceRef = useRef<string[]>(filterByService);
  const [filterStatusMessage, setFilterStatusMessage] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [nextReleaseDate, setNextReleaseDate] = useState<Date>(new Date());
  const [redirectToSub, setRedirectToSub] = useState<string>("");
  const [filterAnnouncement, setFilterAnnouncement] = useState<string>("");
  const [dateFilterTick, setDateFilterTick] = useState(0);
  const [shouldAnnounce, setShouldAnnounce] = useState(false);

  const handleDateFilterChange = (value: any) => {
    setFilterByDate(value);
    setDateFilterTick((t) => t + 1);
    setShouldAnnounce(true);
  };

  const { showReleaseDetails, reset } = useSelector((state: any) => state.staticPages);
  const dispatch = useDispatch()

  const handleAnchorClick = useHandleAnchorClick();
  const tableRef = useRef(null) as any;

  //Need to be able to show releases in future by default,
  //so set whatever "next release" date here (i.e. set to latest of Prod or Gold Release Date)
  let endDate = new Date() < nextReleaseDate ? nextReleaseDate : new Date();

  Array.isArray(data) && data.forEach((item) => {
    const isBat = item.environment.some((env => env.hasOwnProperty('bat')));

    if (!isBat) {
      item.environment.forEach((env) => {
        env.bat = '';
      })
    }
    item.environment.forEach((env, index) => {
      item.environment[index] = {
        id: env.id,
        prod: env.prod,
        bat: env.bat,
        uat: env.uat
      }
    })
  })

  useEffect(() => {
    //update for language switch
    const locSearch = decodeURIComponent(window.location.search?.trim())?.split('?');
    if (locSearch?.length > 1) {
      dispatch(setClickedReleaseItem(cleanDetailsLink(locSearch[locSearch.length - 1])));
      setRedirectToSub(decodeURIComponent(window.location.search?.trim()))
    } else {
      setRedirectToSub(window.location.search?.trim() ? decodeURIComponent(window.location.search?.trim()) : "");
    }


    const sortedData = Array.isArray(data) ? data.sort(sortByPubDate) : [];
    let getLastRlsDate = new Date();
    sortedData?.forEach((d) => {
      d.environment.forEach((e) => {
        const uatDate = new Date(e.uat);
        const batDate = new Date(e.bat)
        const prodDate = new Date(e.prod);
        getLastRlsDate =
          prodDate > getLastRlsDate
            ? prodDate
            : uatDate > getLastRlsDate
              ? uatDate
              : batDate > getLastRlsDate
                ? batDate
                : getLastRlsDate;
      });
    });
    setNextReleaseDate(getLastRlsDate);
    setEntries(sortedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, window.location.search]);



  useEffect(() => {
    //update Release Details content on language switch or direct link to sub
    if (showReleaseDetails || redirectToSub !== "") {
      const parentPath = redirectToSub.split("?")[1];
      [...entries].map((entry) =>
        entry.entries.forEach((sub: any) => {
          if (
            (sub.detailsRoute.length &&
              cleanDetailsLink(sub.detailsRoute) ===
              parentPath) || (
              sub.title.length &&
              cleanDetailsLink(sub.title.trim()) === parentPath)
          ) {
            sub["envDates"] = entry.environment[0];
            setDetailsContent(sub);
            if (!showReleaseDetails && window.location.pathname.includes("releasenotes") && !reset) {
              dispatch(setShowReleaseDetails(true))
            }
          }
        })
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);



  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === tableRef.current) {
          entry.contentRect.width < 838
            ? setIsMobile(true)
            : setIsMobile(false);
        }
      }
    });

    const tableNode = tableRef.current;
    if (tableNode) {
      observer.observe(tableNode);
    }

    return () => {
      if (tableNode) {
        observer.unobserve(tableNode);
      }
    };
  }, [redirectToSub]);

  useEffect(() => {
    const filteredEntriesWithService = filterEntries({
      services: filterByService,
      date: filterByDate,
      announce: shouldAnnounce,
    });

    const serviceFilterChanged = prevFilterByServiceRef.current !== filterByService;
    prevFilterByServiceRef.current = filterByService;

    if (serviceFilterChanged) {
      const resultsCount = filteredEntriesWithService.reduce(
        (total, entry) => total + (entry.entries?.length || 0),
        0
      );

      setFilterStatusMessage(
        filterByService.length === 0
          ? `${t('release_notes.filtersClearedWithCount')}`
          : `${t('release_notes.filtersAppliedWithCount', {
            filterCount: filterByService.length,
            resultCount: resultsCount
          })}`
      );
    }
    setShouldAnnounce(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterByService, filterByDate, dateFilterTick]);

  // When the release detail view is opened, the AppFilterDate picker unmounts
  // and its selected dates are cleared. Reset the date filter here so that when
  // the user returns to the list it isn't stuck in a filtered state that no
  // longer matches the (now cleared) date picker.
  useEffect(() => {
    if (showReleaseDetails) {
      setFilterByDate(undefined);
    }
  }, [showReleaseDetails]);

  const checkIfArray = (item: any) => {
    const itemArray: string[] = [];

    if (Array.isArray(item)) {
      item.forEach((i) => itemArray.push(i.value));
    } else if (typeof item === "object") {
      itemArray.push(item.value);
    } else {
      itemArray.push(item);
    }

    return itemArray;
  };

  const filterEntries = (filterCriteria) => {
    const { date, announce } = filterCriteria;
    let { services } = filterCriteria;
    let startDate = new Date("2023-01-01");

    if (date?.length) {
      // Normalize to inclusive boundaries
      startDate = startOfDay(date[0]);
      endDate = endOfDay(date[1]);
    }

    const filteredEntries = Array.isArray(data) ? data.filter((entry) => {
      const prodDateRaw = entry.environment?.[0]?.prod;
      const uatDateRaw = entry.environment?.[0]?.uat;
      const batDateRaw = entry.environment?.[0]?.bat;

      const prodDate = prodDateRaw ? new Date(prodDateRaw) : null;
      const uatDate = uatDateRaw ? new Date(uatDateRaw) : null;
      const batDate = batDateRaw ? new Date(batDateRaw) : null;

      const valid = (d: Date | null) => d && !isNaN(d.getTime());

      const matchesStartDate = [prodDate, uatDate, batDate].some(
        (d) => valid(d as any) && (d as Date) >= startDate
      );
      const matchesEndDate = [prodDate, uatDate, batDate].some(
        (d) => valid(d as any) && (d as Date) <= endDate
      );

      const matchesServices =
        services === null ||
        services.length === 0 ||
        services.some((service) =>
          entry.entries.some((item) => {
            let servicesList = checkIfArray(item.services);
            return servicesList.includes(service);
          })
        );

      return matchesStartDate && matchesEndDate && matchesServices;
    }) : [];

    const filteredEntriesCount = filteredEntries.length;

    let filteredEntriesWithService = filteredEntries;

    if (services.length !== 0) {
      filteredEntriesWithService = filteredEntries.map((entry) => {
        const entriesWithService = entry.entries.filter((ent) =>
          services.some((service) => {
            let servicesList = checkIfArray(ent.services);
            return servicesList.includes(service);
          })
        );
        return { ...entry, entries: entriesWithService };
      });
    }

    setEntries(filteredEntriesWithService);

    if (announce) {
      const message = `Date filter applied. Showing ${filteredEntriesCount} release note${filteredEntriesCount === 1 ? "" : "s"}.`;
      setFilterAnnouncement("");
      window.setTimeout(() => setFilterAnnouncement(message), 50);
    }

    return filteredEntriesWithService;

  };

  const goToInfoPage = (entry, dates) => {

    if (!window.location.pathname.toLowerCase().includes("releasenotes")) {
      return
    }

    delete dates.id;
    entry["envDates"] = dates;
    setDetailsContent(entry);
    dispatch(setShowReleaseDetails(true));
    const newUrl = `/${i18n.language}/api/releasenotes?${cleanDetailsLink(entry.detailsRoute?.trim())}`;
    window.history.pushState({}, "", newUrl);
    window.scrollTo({ top: 0, behavior: "smooth" });
    dispatch(setReleaseNoteTitle(entry.title));
  };

  const servicesLink = (item, title) => {
    return (
      <div key={`servLink-${item}`}>
        <a
          href={`/${i18n.language
            }/api/documentation/${getServiceLink(item)}`}
          style={{ textDecoration: "none", wordBreak: "break-word" }}
          aria-label={`${item} for ${title}`}
        >
          {item}
        </a>
        <br />
      </div>
    );
  };

  const servicesLinkMobile = (item) => {
    return (
      <div key={item}>
        <a
          href={`/${i18n.language
            }/api/documentation/${getServiceLink(item)}`}
          style={{ textDecoration: "none" }}
        >
          {item}
        </a>
        <br />
      </div>
    );
  };

  function sortByPubDate(a, b) {
    return a.date === b.date ? 0 : a.date > b.date ? 1 : -1;
  }

  return Array.isArray(entries) ? (
    <div ref={tableRef}>
      {!showReleaseDetails && (
        <div
          style={
            {
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
              marginBottom: "5px",
              "--marginRight": isMobile ? "0" : "10px",
            } as any
          }
          className="filter-container"
        >
          <h2 style={{ fontSize: "1rem", fontWeight: "400" }}><AppFilterDate setFilter={handleDateFilterChange} futureDefault={endDate} /></h2>
          <Box style={{ fontSize: "1rem", fontWeight: "400" }}><StatusFilter setFilter={setFilterByService} /></Box>
          <Box aria-live="polite" sx={visuallyHidden}>{filterStatusMessage}</Box>
        </div>
      )}
      <div role="status" aria-live="polite" aria-atomic="true" style={visuallyHidden as any}>
        {filterAnnouncement}
      </div>
      <div aria-live="polite" aria-atomic="true">
        {(!entries || entries.length === 0) && <h3 style={{ margin: "20px 0", textAlign: "center", fontSize: "1rem" }}>{json.noData}</h3>}
      </div>
      {isMobile ? (
        !showReleaseDetails ? (
          <div className="mobile-release-notes">
            {!entries || entries?.length === 0 ? null : (
              [...entries].reverse().map((entry, i) => (
                <div className="mobile-table" key={`t-${i}`}>
                  <div className="mobile-row-container">
                    <Typography className="mobile-row-header">
                      {t('release_notes.dateLabel')}
                    </Typography>
                    {Object.keys(entry.environment[0])
                      .map(
                        (environment, i) =>
                          environment !== "id" && (
                            <Chip
                              key={`c-${i}`}
                              type={
                                environment.toUpperCase()
                              }
                              date={entry.environment[0][environment]}
                            />
                          )
                      )}
                  </div>
                  {entry.entries.map((subEntry, i) => {
                    if (!Array.isArray(subEntry["services"])) {
                      subEntry["services"] = [subEntry["services"]];
                    }

                    return (
                      <div key={i}>
                        <div className="mobile-row-container">
                          <Typography className="mobile-row-header">
                            {t('release_notes.releaseSummary')}
                          </Typography>
                          <div
                            style={{
                              color: "#005078",
                              fontSize: "14px",
                              paddingBottom: "8px",
                              fontWeight: "600",
                              display: "flex",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              goToInfoPage(subEntry, entry.environment[0]);
                            }}
                          >
                            {subEntry.title.trim() !== "none" && subEntry.title}
                            {subEntry.title.trim() !== "" &&
                              subEntry.title.trim() !== "none" && (
                                <img
                                  className="rlsNotesLinkIcon"
                                  src="/images/icons/linkIcon.png"
                                  alt="Link Icon"
                                />
                              )}
                          </div>
                          <Typography
                            className=""
                            dangerouslySetInnerHTML={{ __html: subEntry.summary }}
                            onClick={handleAnchorClick}
                          ></Typography>
                        </div>
                        {subEntry.implementation !== "" && (
                          <div className="mobile-row-container">
                            <Typography className="mobile-row-header">
                              {t('release_notes.implementationReq')}
                            </Typography>
                            <Typography
                              className=""
                              dangerouslySetInnerHTML={{
                                __html: subEntry.implementation,
                              }}
                              onClick={handleAnchorClick}
                            ></Typography>
                          </div>
                        )}
                        <div className="mobile-row-container">
                          <Typography className="mobile-row-header">
                            {t('release_notes.apiAffected')}
                          </Typography>
                          <div>
                            {subEntry.services.map((item, ind) => {
                              let servicesList = checkIfArray(item);

                              return (
                                <React.Fragment key={ind}>
                                  {servicesList
                                    .sort()
                                    .map((s) => servicesLinkMobile(s))}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )))}
          </div>
        ) : (
          <ReleaseNotesDetail
            details={detailsContent}
            Chip={Chip}
            lang={i18n.language}
            setRedirectToSub={setRedirectToSub}
          />
        )
      ) : !showReleaseDetails ? (
        <>
          <span id="releaseNotes" style={{ position: "absolute", left: "-9999px" }}>{t('release_notes.caption')}</span>
          {!entries || entries?.length === 0 ? null :
            <table className="release-notes-desktop" aria-labelledby="releaseNotes">
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "50%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">
                    {t('release_notes.dateLabel')}
                  </th>
                  <th scope="col">
                    {t('release_notes.releaseSummary')}
                  </th>
                  <th scope="col">
                    {t('release_notes.implementationReq')}
                  </th>
                  <th scope="col">
                    {t('release_notes.apiAffected')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {
                  [...entries]?.reverse()?.map((items, i) =>
                    items.entries.map((entry, j) => {
                      if (!Array.isArray(entry["services"])) {
                        entry["services"] = [entry["services"]];
                      } else {
                        entry["services"] = entry["services"].sort((a, b) =>
                          a.value > b.value ? 0 : -1
                        );
                      }
                      if (
                        entry.detailsRoute.length &&
                        cleanDetailsLink(entry.detailsRoute) ===
                        redirectToSub.replace("?", "")
                      ) {
                        goToInfoPage(entry, items.environment[0]);
                      }
                      const isLastRow = items.entries.length - 1 === j;
                      const hasMultipleEntries = items?.entries?.length > 1;
                      const rowStyle = {
                        verticalAlign: "top",
                        border: hasMultipleEntries && !isLastRow ? "none" : "",
                      };
                      return (
                        <tr
                          key={`${i}-${j}`}
                          className={i % 2 === 0 ? "odd-row" : "even-row"}
                        >
                          {j === 0 ? (
                            <td style={rowStyle}>
                              {Object.keys(items.environment[0])
                                .map(
                                  (environment, ind) =>
                                    environment !== "id" && (
                                      <Chip
                                        key={`${i}-${j}-${ind}`}
                                        type={
                                          environment.toUpperCase()
                                        }
                                        date={items.environment[0][environment]}
                                      />
                                    )
                                )}
                            </td>
                          ) : (
                            <td
                              style={{ ...rowStyle, backgroundColor: "inherit" }}
                            ></td>
                          )}

                          <td style={{ verticalAlign: "top" }}>
                            <h2
                              style={{
                                color: "#005078",
                                marginBottom: "8px",
                                fontWeight: "600",
                                display: "flex",
                                cursor: "pointer",
                                fontSize: "16px",
                              }}
                              onClick={() =>
                                goToInfoPage(entry, items.environment[0])
                              }
                              onKeyDown={(e) =>
                                e.key === "Enter" &&
                                goToInfoPage(entry, items.environment[0])
                              }
                              role="link"
                              tabIndex={0}
                            >
                              {" "}
                              {entry.title.trim() !== "none" && entry.title}
                              {entry.title !== "" &&
                                entry.title.trim() !== "none" && (
                                  <img
                                    className="rlsNotesLinkIcon"
                                    src="/images/icons/linkIcon.png"
                                    alt="Link Icon"
                                  />
                                )}
                            </h2>
                            <div
                              dangerouslySetInnerHTML={{ __html: entry.summary }}
                              onClick={handleAnchorClick}
                            ></div>
                          </td>
                          <td
                            dangerouslySetInnerHTML={{ __html: entry.implementation }}
                            onClick={handleAnchorClick}
                            style={{ verticalAlign: "top" }}
                          ></td>
                          <td style={{ verticalAlign: "top" }}>
                            {entry.services.sort().map((i, ind) => {
                              const serviceList = checkIfArray(i);
                              return (
                                <React.Fragment key={ind}>
                                  {serviceList.map((s) => servicesLink(s, entry.title))}
                                </React.Fragment>
                              );
                            })}
                          </td>
                        </tr>
                      );
                    })
                  )}
              </tbody>
            </table>
          }
        </>
      ) : (
        <ReleaseNotesDetail
          details={detailsContent}
          Chip={Chip}
          lang={i18n.language}
          setRedirectToSub={setRedirectToSub}
        />
      )}
    </div>
  ) : (
    <></>
  );
};

const Chip = ({ type, date }) => {
  const color = {
    UAT: "#E17000",
    PROD: "#20842F",
    BAT: '#00778F',
  };

  const { i18n } = useTranslation();
  const dateFormat = new Date(`${date} 00:00:00`);
  const isDateAvailable = date && date.trim();
  const chipColor = color[type];

  const enMonth = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "June",
    "July",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const frMonth = [
    "janv",
    "févr",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "sept",
    "oct",
    "nov",
    "déc",
  ];

  const labelMap = {
    fr: {
      months: frMonth[dateFormat.getMonth()],
    },
    en: {
      months: enMonth[dateFormat.getMonth()],
    }
  }
  const currentLanguage = i18n.language || "en";
  const months = labelMap[currentLanguage].months;
  const month = months;

  // const month =
  //   i18n.language === "fr"
  //     ? frMonth[dateFormat.getMonth()]
  //     : enMonth[dateFormat.getMonth()];

  const day =
    dateFormat.getDate().toString().length < 2
      ? `0${dateFormat.getDate()}`
      : dateFormat.getDate();

  const dateString = `${day} ${month} ${dateFormat.getFullYear()}`;
  const releaseDate = isDateAvailable ? dateString : "TBD";
  const chipLabel = `${type} environment release date: ${releaseDate}`;

  return (
    <div className="chip-container" aria-label={chipLabel}>
      <span className="chip-type" style={{ backgroundColor: chipColor }}>
        {type}
      </span>
      <span
        className="chip-date"
        style={{ borderColor: chipColor }}
      >
        {releaseDate}
      </span>
    </div>
  );
};

export default ReleaseNotes;
